import { type FC, memo, type ReactNode } from 'react';
import cx from 'classnames';

import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { GenerationToolLabel } from 'src/components/GenerationToolLabel';
import { SvgStringToHtml } from 'src/components/SvgStringToHtml';
import { TruncatedTooltip } from 'src/components/TruncatedTooltip';
import {
  type GenerationTool,
  getToolLabel,
  getToolSectionLabel,
  mapGenerationTool,
} from 'src/data/generationTool';

import { useStyles } from './useStyles';

interface Props {
  className?: string;
  dataTestId?: string;
  classes?: Partial<ReturnType<typeof useStyles>>;
  tool: GenerationTool;
  boldTitle?: boolean;
  toolTitle?: string;
  hideIcon?: boolean;
  iconImage?: string;
  replaceIcon?: ReactNode;
}

export const GenerationToolItem: FC<Props> = memo(
  ({
    className,
    classes,
    tool,
    boldTitle = false,
    toolTitle,
    hideIcon = false,
    iconImage,
    replaceIcon,
  }) => {
    const styles = useStyles({ classes });

    const mappedTool = mapGenerationTool(tool);

    const label = toolTitle || getToolSectionLabel(mappedTool);
    const tooltip = label || getToolLabel(mappedTool);

    return (
      <div
        className={cx(styles.root, className)}
        data-testid={`GenerationToolItem_${tool}`}
      >
        {!hideIcon &&
          (replaceIcon ? (
            replaceIcon
          ) : iconImage ? (
            <SvgStringToHtml
              className={cx(styles.icon, styles.iconSvgString)}
              svgString={iconImage}
            />
          ) : (
            <GenerationToolIcon
              className={styles.icon}
              tool={mappedTool}
            />
          ))}

        <TruncatedTooltip title={tooltip}>
          {(ref) => (
            <GenerationToolLabel
              className={styles.label}
              ref={ref}
              label={label}
              boldLabel={boldTitle}
            />
          )}
        </TruncatedTooltip>
      </div>
    );
  }
);
